import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  User, 
  Building, 
  Camera, 
  Check, 
  Loader2,
  Crown,
  Globe,
  Phone,
  LogOut,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countryCodes = [
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+1', country: 'Estados Unidos', flag: '🇺🇸' },
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+502', country: 'Guatemala', flag: '🇬🇹' },
];

export default function ProfilePage() {
  const { user, updateProfile, isPro, isTrial, trialDaysLeft, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    workshop_name: user?.workshop_name || '',
    country_code: user?.country_code || '+52',
    phone: user?.phone || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateProfile(formData);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const uploadRes = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      
      if (!uploadRes.ok) throw new Error('Error al subir imagen');
      
      const { url } = await uploadRes.json();
      
      await updateProfile({ workshop_logo: `${API_URL}${url}` });
      toast.success('Logo actualizado');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubscribe = async (planType = 'monthly') => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin, plan_type: planType })
      });

      if (!response.ok) {
        throw new Error('Error al crear sesión de pago');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="animate-fade-in max-w-2xl" data-testid="profile-page">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleLogoUpload}
      />
      
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Mi Perfil</h1>

      <div className="space-y-6">
        {/* Subscription Card */}
        <div className={`rounded-xl p-6 ${user?.subscription_plan === 'pro' ? 'subscription-card' : 'card-base border-amber-200 shadow-sm'}`}>
          {user?.subscription_plan === 'pro' ? (
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-6 h-6 text-amber-400" />
                <span className="text-xl font-bold">Plan PRO Activo</span>
              </div>
              {user?.subscription_expires_at && (
                <p className="text-white/80 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Válido hasta: {format(new Date(user.subscription_expires_at), 'PPP', { locale: es })}
                </p>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {user?.subscription_plan === 'trial' ? 'Prueba Gratuita PRO' : 'Prueba Expirada'}
                  </h3>
                  {user?.subscription_plan === 'trial' ? (
                    <p className="text-sm text-amber-600 font-medium">
                      Te quedan {trialDaysLeft} días de acceso total
                    </p>
                  ) : (
                    <p className="text-sm text-red-500 font-medium">
                      Actualiza a PRO para seguir utilizando el sistema
                    </p>
                  )}
                </div>
                {user?.subscription_plan === 'trial' ? (
                  <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider">TRIAL</span>
                ) : (
                  <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider">EXPIRADO</span>
                )}
              </div>
              
              <div className="flex flex-col gap-3 mt-6">
                <Button 
                  onClick={() => handleSubscribe('monthly')}
                  className="w-full bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-500 hover:to-amber-700 font-semibold shadow-md"
                  data-testid="subscribe-monthly-btn"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  Activar PRO Mensual - $249 MXN
                </Button>
                <Button 
                  onClick={() => handleSubscribe('annual')}
                  className="w-full border-2 border-amber-500 text-amber-700 hover:bg-amber-50 hover:border-amber-600 font-semibold"
                  variant="outline"
                  data-testid="subscribe-annual-btn"
                >
                  <Crown className="w-5 h-5 mr-2" />
                  PRO Anual - $2,490 MXN (Ahorra 2 meses)
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Profile Form */}
        <div className="card-base p-6">
          <h2 className="font-semibold text-slate-900 mb-4">Información Personal</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Logo Upload */}
            <div className="flex items-center gap-4 mb-6">
              <div 
                className={`logo-upload ${user?.workshop_logo ? 'logo-upload-active' : ''}`}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                ) : user?.workshop_logo ? (
                  <img 
                    src={user.workshop_logo} 
                    alt="Logo" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Camera className="w-6 h-6 text-slate-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-900">Logo del taller</p>
                <p className="text-sm text-slate-500">Haz clic para subir</p>
              </div>
            </div>
            
            <div>
              <Label htmlFor="name">Nombre completo</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10"
                  data-testid="profile-name-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="workshop_name">Nombre del taller</Label>
              <div className="relative mt-1">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="workshop_name"
                  value={formData.workshop_name}
                  onChange={(e) => setFormData({ ...formData, workshop_name: e.target.value })}
                  className="pl-10"
                  data-testid="profile-workshop-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="country_code">País (código de teléfono)</Label>
              <Select
                value={formData.country_code}
                onValueChange={(value) => setFormData({ ...formData, country_code: value })}
              >
                <SelectTrigger className="mt-1" data-testid="profile-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.flag} {c.country} ({c.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Este código se usará automáticamente para WhatsApp de clientes y proveedores
              </p>
            </div>

            <div>
              <Label htmlFor="phone">Tu número de teléfono (10 dígitos)</Label>
              <div className="flex gap-2 mt-1">
                <div className="w-20 px-3 py-2 bg-slate-100 rounded-md text-sm text-slate-600 flex items-center justify-center">
                  {formData.country_code}
                </div>
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    className="pl-10"
                    placeholder="5512345678"
                    maxLength={10}
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Para recibir novedades y notificaciones de TallerMec Pro
              </p>
            </div>
            
            <div className="pt-2">
              <Label className="text-slate-500">Email</Label>
              <p className="text-slate-900">{user?.email}</p>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#1E3A5F] hover:bg-[#162e4d]"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </form>
        </div>

        {/* Logout - Mobile only shows here since menu doesn't have it */}
        <div className="md:hidden">
          <Button 
            variant="outline" 
            onClick={logout}
            className="w-full text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
}
